/*=== CORE IMPORTS ===*/
import { html, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { q1LogItemCardStyles } from './q1-log-item-card-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';

/*=== Q1 COMPONENTS ===*/
import '../q1-markdown/q1-markdown.js';

/*=== CONSTANTS ===*/

// None

/*=== COMPONENT CLASS ===*/

export class Q1LogItemCard extends LitElement {
  static styles = [ q1LogItemCardStyles ];

  static properties = {    

    /**
     * The log item object to display. Must be enriched with the `enrichLogItem()` helper.
     * NOTE: To display the author avatars you'll need to use the provide the authorMembersByRef 
     * param to `enrichLogItem()`.
     */
    logItem: { type: Object },

    /** Set this to suppress the link on the log item card. */
    noLink: { type: Boolean, attribute: 'no-link' },

    /** Set this in order to force the focus/hover state to always be active. */
    alwaysActive: { type: Boolean, attribute: 'always-active' },

  };

  constructor() {
    super();

    this.logItem = null;

    this.noLink = false;
    this.alwaysActive = false;
  }

  handleLinkClick(event) {
    if (this.noLink) event.preventDefault();
  }

  get authorsByLine() {
    if (!this.logItem?.authors_members?.length) return null;

    // Generate a string of the format: 'By Author1, Author2 & Author3'
    const authorNames = this.logItem.authors_members.map((member) => member?.name).filter(n => n);
    if (authorNames.length >= 3) {
      const [secondToLastName, lastName] = authorNames.splice(-2);
      authorNames.push(`${secondToLastName} & ${lastName}`);
    }
    return `By ${authorNames.join(', ')}`;
  }

  render() {
    return html`
      <a
        id="log-item-card"
        part="card"
        href="/li/${this.logItem?.id}"
        @click=${this.handleLinkClick}
        class=${classMap({ 'always-active': this.alwaysActive })}
      >
        <div part="info" id="log-item-card-info">
          <div id="timestamp" class="horizontal">
            <sl-icon library="material" name="calendar_month"></sl-icon>
            ${this.logItem.status_timestampLabel}
            <sl-format-date
              date=${this.logItem.status_timestamp}
              month="short" day="numeric" year="numeric"
            ></sl-format-date>
          </div>

          <div id="headline">${this.logItem.headline}</div>

          ${!this.logItem.authors_members?.length ? null : html`
            <div id="authors" class="horizontal">
              <avatar-stack>
                ${this.logItem.authors_members.map((author) => html`
                  <sl-avatar image=${author?.profile?.mini?.url} shape="circle"></sl-avatar>
                `)}
              </avatar-stack>
              ${this.authorsByLine}
            </div>
          `}

          <div id="reading-time" class="horizontal">
            <sl-icon library="material" name="schedule"></sl-icon>
            ${this.logItem.readingTime_text}
          </div>

          <div id="body">
            <q1-markdown variant="text" .content=${this.logItem.body_firstParagraph}></q1-markdown>
          </div>
        </div>
      </a>
    `;
  }

}

customElements.define('q1-log-item-card', Q1LogItemCard);
